import DayViewItemsLayer from 'mini-day-view/lib/components/DayViewItemsLayer';
import React from 'react';
import ScrollIntoViewInfo, { ScrollIntoView } from 'mini-day-view/lib/schema/ScrollIntoView';
import strings from 'owa-strings/owa-mini-common-strings';
import TimeGridLayout from 'mini-day-view/lib/components/TimeGridLayout';
import { addDays, differenceInMinutes, formatShortHour, OwaDate, startOfDay } from 'owa-datetime';
import { CalendarEvent } from 'mini-calendar-event-types';
import { DayViewEventCard, groupEventsByAllDayProperty } from 'mini-calendar-day-surface';
import { DEFAULT_PIXELS_PER_MINUTE } from 'mini-day-view';
import { format } from 'owa-strings';
import { getRsvpDayViewItemAriaLabel } from '../utils/getRsvpDayViewItemAriaLabel';
import { GridLayerInfo } from 'mini-day-view/lib/schema/GridLayerInfo';
import { isInDayItemCurrentItem } from '../utils/isInDayItemCurrentItem';
import { mergeStyles } from '@uifabric/styling';
import { useDataFetchState } from 'mini-hooks';
import { useStopEventBackgroundPropagation } from 'mini-hooks/lib/useStopEventBackgroundPropagation';
import ColorScheme, {
    AccessibleTextSchemeBackground,
    getAccessibleTextColor,
} from 'mini-color-utils/lib/ColorScheme';

const GREY_PRIMARY_HEXCODE: string = '#666666';
const INTER_LINE_DISPLACEMENT_IN_MIN = 30;

const styles = require('./RsvpDayView.scss');
const classNames = require('classnames');

interface RsvpDayViewProps {
    item: CalendarEvent;
    colorScheme: ColorScheme;
    getCalendarEvents: (start: OwaDate, end: OwaDate) => Promise<CalendarEvent[]>;
}

export const RsvpDayView = (props: RsvpDayViewProps) => {
    const { item, colorScheme, getCalendarEvents } = props;
    const { Start } = item;
    const startOfEventDate = startOfDay(Start);
    const endOfEventDate = addDays(startOfEventDate, 1);

    // Fetch events for the day
    const { data: calendarItems } = useDataFetchState(
        [] /* Initial data */,
        false /* debounce not required */,
        getCalendarEvents,
        [Start],
        startOfEventDate,
        endOfEventDate
    );

    const { inDayItems } = groupEventsByAllDayProperty(calendarItems);

    const range = {
        start: startOfEventDate,
        end: endOfEventDate,
    };

    const scrollIntoViewInfo: ScrollIntoViewInfo = {
        topPosition: differenceInMinutes(Start, startOfEventDate) * DEFAULT_PIXELS_PER_MINUTE,
        scrollIntoViewType: ScrollIntoView.Middle,
    };

    const gridLayerInfo: GridLayerInfo = {
        repeatIntervalInMin: INTER_LINE_DISPLACEMENT_IN_MIN,
        firstLineStartTime: startOfEventDate,
        formatTime: formatShortHour,
    };

    const renderers = {
        inDayItemRenderer: (
            inDayItem: CalendarEvent,
            getItemAriaLabel: (itemInfo: string) => string
        ) => inDayItemRenderer(inDayItem, item, colorScheme, getItemAriaLabel),
    };

    const getItemAriaLabel = (itemInfo: string, itemIndex: number): string =>
        format(
            strings.calendarSurfaceItemPositionalAriaLabel,
            itemIndex + 1,
            inDayItems.length,
            itemInfo
        );

    const layers = [
        {
            view: (
                <DayViewItemsLayer
                    renderers={renderers}
                    dayViewItems={inDayItems}
                    getItemAriaLabel={getItemAriaLabel}
                    pixelPerMin={DEFAULT_PIXELS_PER_MINUTE}
                />
            ),
            depth: 3,
            ariaHidden: false,
            key: 'DayViewItemsLayer',
        },
    ];

    const stopPropagationElementRef = useStopEventBackgroundPropagation();

    return (
        <div className={styles.rsvpDayView}>
            <div
                className={styles.dayViewSurfaceContainer}
                ref={stopPropagationElementRef}
                onClick={ev => ev?.stopPropagation()}
                role="none">
                <TimeGridLayout
                    range={range}
                    gridLayerInfo={gridLayerInfo}
                    scrollIntoViewInfo={scrollIntoViewInfo}
                    layers={layers}
                    pixelsPerMin={DEFAULT_PIXELS_PER_MINUTE}
                />
            </div>
        </div>
    );
};

const inDayItemRenderer = (
    inDayItem: CalendarEvent,
    currentItem: CalendarEvent,
    colorScheme: ColorScheme,
    getItemAriaLabel: (itemInfo: string) => string
) => {
    const renderBorder = () => renderCustomBorder(colorScheme);
    const getAriaLabel = () =>
        getItemAriaLabel(getRsvpDayViewItemAriaLabel(inDayItem, currentItem));
    const onClick = (id: string) => {};

    return isInDayItemCurrentItem(inDayItem, currentItem) ? (
        <DayViewEventCard
            item={inDayItem}
            hideFreeBusyBar
            hideBorder
            pixelsPerMinute={DEFAULT_PIXELS_PER_MINUTE}
            onRenderAdditionalContent={renderBorder}
            itemAriaLabelGetter={getAriaLabel}
            onClick={onClick}
        />
    ) : (
        <DayViewEventCard
            item={inDayItem}
            primaryColorForCustomColorScheme={GREY_PRIMARY_HEXCODE}
            pixelsPerMinute={DEFAULT_PIXELS_PER_MINUTE}
            itemAriaLabelGetter={getAriaLabel}
            onClick={onClick}
        />
    );
};

const renderCustomBorder = (colorScheme: ColorScheme) => {
    const borderColor = getAccessibleTextColor(
        colorScheme,
        AccessibleTextSchemeBackground.schemeLighter
    );
    const leftRightGradient = mergeStyles({
        backgroundImage: `linear-gradient(0deg, ${borderColor} 50%, transparent 0%)`,
    });
    const topBottomGradient = mergeStyles({
        backgroundImage: `linear-gradient(90deg, ${borderColor} 50%, transparent 0%)`,
    });
    return (
        <>
            <div className={classNames(styles.msThemeLeftBorder, leftRightGradient)} />
            <div className={classNames(styles.msThemeRightBorder, leftRightGradient)} />
            <div className={classNames(styles.msThemeTopBorder, topBottomGradient)} />
            <div className={classNames(styles.msThemeBottomBorder, topBottomGradient)} />
        </>
    );
};
